import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Chip,
  Avatar,
  AvatarGroup,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import teamService, { Team, TeamDetails, CreateTeamRequest, UpdateTeamRequest, AddTeamMemberRequest } from '../services/teamService';
import { getUsers, User } from '../services/userService';

const TeamsPage: React.FC = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openMembersDialog, setOpenMembersDialog] = useState(false);
  const [openAddMemberDialog, setOpenAddMemberDialog] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<TeamDetails | null>(null);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [formData, setFormData] = useState<CreateTeamRequest>({
    name: '',
    description: '',
    leaderId: undefined,
  });
  const [addMemberData, setAddMemberData] = useState<AddTeamMemberRequest>({
    userId: '',
    role: 'member',
  });

  useEffect(() => {
    loadTeams();
    loadAvailableUsers();
  }, []);

  const loadTeams = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await teamService.getAllTeams();
      setTeams(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const users = await getUsers();
      setAvailableUsers(users);
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setError('Failed to load users');
    }
  };

  const handleOpenDialog = (team?: Team) => {
    if (team) {
      setEditingTeam(team);
      setFormData({
        name: team.name,
        description: team.description || '',
        leaderId: team.leaderId,
      });
    } else {
      setEditingTeam(null);
      setFormData({
        name: '',
        description: '',
        leaderId: undefined,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTeam(null);
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      // Clean form data - convert empty strings to undefined for optional fields
      const cleanedData = {
        ...formData,
        leaderId: formData.leaderId || undefined,
        description: formData.description || undefined,
      };
      
      if (editingTeam) {
        await teamService.updateTeam(editingTeam.id, cleanedData);
      } else {
        await teamService.createTeam(cleanedData);
      }
      await loadTeams();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save team');
      console.error('Error saving team:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    
    setError(null);
    try {
      await teamService.deleteTeam(id);
      await loadTeams();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete team');
    }
  };

  const handleOpenMembers = async (team: Team) => {
    setError(null);
    try {
      const teamDetails = await teamService.getTeamById(team.id);
      setSelectedTeam(teamDetails);
      setOpenMembersDialog(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load team members');
    }
  };

  const handleCloseMembersDialog = () => {
    setOpenMembersDialog(false);
    setSelectedTeam(null);
  };

  const handleOpenAddMember = () => {
    setAddMemberData({
      userId: '',
      role: 'member',
    });
    setOpenAddMemberDialog(true);
  };

  const handleCloseAddMember = () => {
    setOpenAddMemberDialog(false);
  };

  const handleAddMember = async () => {
    if (!selectedTeam || !addMemberData.userId) return;
    
    setError(null);
    try {
      await teamService.addTeamMember(selectedTeam.id, addMemberData);
      const updatedTeam = await teamService.getTeamById(selectedTeam.id);
      setSelectedTeam(updatedTeam);
      handleCloseAddMember();
      await loadTeams();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selectedTeam) return;
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    
    setError(null);
    try {
      await teamService.removeTeamMember(selectedTeam.id, userId);
      const updatedTeam = await teamService.getTeamById(selectedTeam.id);
      setSelectedTeam(updatedTeam);
      await loadTeams();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove member');
    }
  };

  if (loading && teams.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Teams
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create Team
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {teams.length === 0 && !loading ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <GroupIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No teams yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first team to start organizing your members
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Create Team
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Leader</TableCell>
                <TableCell align="center">Members</TableCell>
                <TableCell align="center">Projects</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teams.map((team) => (
                <TableRow key={team.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/teams/${team.id}`)}>
                  <TableCell>
                    <Typography variant="subtitle2">{team.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                      {team.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ width: 28, height: 28 }}>
                        <PersonIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="body2">{team.leaderName || '—'}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={team.memberCount}
                      size="small"
                      color="primary"
                      onClick={() => handleOpenMembers(team)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={team.activeProjectsCount} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(team.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Members">
                      <IconButton size="small" onClick={() => handleOpenMembers(team)}>
                        <GroupIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog(team)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(team.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Team Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTeam ? 'Edit Team' : 'Create New Team'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Team Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Team Leader</InputLabel>
              <Select
                value={formData.leaderId || ''}
                onChange={(e) => setFormData({ ...formData, leaderId: e.target.value || undefined })}
                label="Team Leader"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {availableUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.firstName} {user.lastName} ({user.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name.trim()}
          >
            {editingTeam ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Team Members Dialog */}
      <Dialog open={openMembersDialog} onClose={handleCloseMembersDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedTeam?.name} - Members
            </Typography>
            <Button
              startIcon={<PersonAddIcon />}
              onClick={handleOpenAddMember}
              size="small"
            >
              Add Member
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTeam && selectedTeam.members.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No members in this team yet
              </Typography>
            </Box>
          ) : (
            <List>
              {selectedTeam?.members.map((member) => (
                <ListItem key={member.id}>
                  <ListItemAvatar>
                    <Avatar src={member.userProfilePicture}>
                      {member.userFullName.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={member.userFullName}
                    secondary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          {member.userEmail}
                        </Typography>
                        <Chip label={member.role} size="small" />
                      </Stack>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveMember(member.userId)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMembersDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={openAddMemberDialog} onClose={handleCloseAddMember} maxWidth="xs" fullWidth>
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Select User</InputLabel>
              <Select
                value={addMemberData.userId}
                onChange={(e) => setAddMemberData({ ...addMemberData, userId: e.target.value })}
                label="Select User"
              >
                {availableUsers
                  .filter(user => !selectedTeam?.members.some(m => m.userId === user.id))
                  .map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={addMemberData.role}
                onChange={(e) => setAddMemberData({ ...addMemberData, role: e.target.value })}
                label="Role"
              >
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="lead">Lead</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddMember}>Cancel</Button>
          <Button
            onClick={handleAddMember}
            variant="contained"
            disabled={!addMemberData.userId}
          >
            Add Member
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamsPage;
